/**
 * Pure helpers for rendering a seat reply's markdown in the Roost chat
 * bubble and for stripping it down to something safe to hand to
 * text-to-speech (no raw URLs, no pipe/dash table noise). No NativeScript or
 * Angular imports here, so both the render path and the speech path stay
 * unit-testable off-device.
 */
import type {
  ChatMarkdownImage,
  ChatMarkdownSegment,
  ChatMarkdownTable,
} from './chat-markdown.models';

// Bug (ADO thread 51356, finding 2): the repeated group was `(...)+`, which
// needs at least one internal "cell|" before the final cell — i.e. 2+
// columns. A one-column table ("| Name |\n| --- |") has no internal pipe at
// all, so it never matched and stayed raw markdown (speech read the pipes).
// `(...)*` makes the internal group optional so a single-cell row matches.
const TABLE_SEPARATOR_ROW =
  /^\s*\|?(?:\s*:?-{2,}:?\s*\|)*\s*:?-{2,}:?\s*\|?\s*$/;
// A fenced code block opener, e.g. ```, ```typescript, ~~~ or ````. Captures
// the marker run so the fence closes only on the same character at least as
// long (CommonMark; ADO thread 51425). CODE_FENCE (further down) strips a
// fence line from speech, this one tracks fence state.
const CODE_FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})/;

/** True when `line` closes a fence opened by `opener` (e.g. "````"). */
function closesFence(line: string, opener: string): boolean {
  const match = /^ {0,3}(`{3,}|~{3,})\s*$/.exec(line);
  return (
    match !== null &&
    match[1][0] === opener[0] &&
    match[1].length >= opener.length
  );
}

function isTableRow(line: string): boolean {
  return line.includes('|') && line.trim().length > 0;
}

function splitTableRow(line: string): string[] {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map((cell) => cell.trim());
}

// A line that is nothing but a single markdown image: the common shape for
// an image a seat drops into a reply. An image mixed into a paragraph's
// running text is left inside its 'markdown' segment, not pulled out inline,
// since splitting a sentence mid-line is worse than leaving MarkdownView (or
// its bug-34574 plain-Label fallback) show it. At most three leading spaces
// (ADO thread 51438): four spaces or a tab make the line indented code.
const STANDALONE_IMAGE_LINE =
  /^ {0,3}!\[([^\]]*)]\(([^)\s]+)(?:\s+"[^"]*")?\)\s*$/;

function parseStandaloneImage(line: string): ChatMarkdownImage | null {
  const match = STANDALONE_IMAGE_LINE.exec(line);
  if (!match) return null;
  const [, alt, url] = match;
  return { url, alt: alt.trim(), allowed: /^https:\/\//i.test(url) };
}

type MarkdownLineBlock =
  | { kind: 'text'; lines: string[] }
  // Bug 35041: a fenced code block, fence lines included in `lines`; `body`
  // is only what sits between them. Render treats it as text; speech never
  // reads `body`.
  | { kind: 'code'; lines: string[]; body: string[] }
  | { kind: 'table'; table: ChatMarkdownTable }
  | { kind: 'image'; image: ChatMarkdownImage };

/**
 * Splits markdown into contiguous text runs, table blocks and standalone
 * image lines. Shared core so the render path (segmentMarkdownForRender) and
 * the speech path (stripMarkdownForSpeech) can't disagree on what counts as
 * a table or an image.
 *
 * Bug (ADO thread 51356, finding 3): a pipe table INSIDE a fenced code block
 * (```...```) was still recognized as a real table, because table detection
 * never looked at fence state. While a fence is open every line — including
 * a line that looks like a table row or separator — is left in the text
 * block; the fence only closes on the same marker character, at least as
 * long as the opener (thread 51425: ~~~ fences, ```` wrapping ```). The
 * same fence state gates image extraction (thread 51438), so a fenced
 * `![x](https://...)` sample stays text and never starts a load.
 */
function parseMarkdownLineBlocks(markdown: string): MarkdownLineBlock[] {
  const lines = markdown.split('\n');
  const blocks: MarkdownLineBlock[] = [];
  let textBuffer: string[] = [];
  let fence: string | null = null;
  let code: { lines: string[]; body: string[] } | null = null;

  const flushText = () => {
    if (textBuffer.length) {
      blocks.push({ kind: 'text', lines: textBuffer });
      textBuffer = [];
    }
  };

  // An unclosed fence runs to the end of the message (CommonMark), so the
  // final flush below still emits it as code.
  const flushCode = () => {
    if (code) {
      blocks.push({ kind: 'code', ...code });
      code = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (fence !== null && code !== null) {
      code.lines.push(line);
      if (closesFence(line, fence)) {
        fence = null;
        flushCode();
      } else {
        code.body.push(line);
      }
      continue;
    }
    const opener = CODE_FENCE_OPEN.exec(line);
    if (opener) {
      flushText();
      fence = opener[1];
      code = { lines: [line], body: [] };
      continue;
    }
    const image = parseStandaloneImage(line);
    if (image) {
      flushText();
      blocks.push({ kind: 'image', image });
      continue;
    }
    const next = lines[i + 1];
    if (
      isTableRow(line) &&
      next !== undefined &&
      TABLE_SEPARATOR_ROW.test(next)
    ) {
      flushText();
      const headers = splitTableRow(line);
      const rows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j]) && lines[j].trim()) {
        rows.push(splitTableRow(lines[j]));
        j++;
      }
      blocks.push({ kind: 'table', table: { headers, rows } });
      i = j - 1;
      continue;
    }
    textBuffer.push(line);
  }
  flushCode();
  flushText();
  return blocks;
}

/**
 * Splits a seat reply into segments a chat bubble can render: markdown runs
 * go to the native markdown widget as-is (it already handles bold/italic/
 * links/lists), table blocks come back pre-parsed for a custom grid, and a
 * standalone image line (bug 34653) comes back as its own 'image' segment
 * for inline rendering.
 */
export function segmentMarkdownForRender(
  markdown: string
): ChatMarkdownSegment[] {
  const segments: ChatMarkdownSegment[] = [];
  let textBuffer: string[] = [];

  const flushMarkdown = () => {
    const text = textBuffer.join('\n').trim();
    if (text) segments.push({ kind: 'markdown', text });
    textBuffer = [];
  };

  for (const block of parseMarkdownLineBlocks(markdown)) {
    // A code block stays in the markdown run, fences and all, exactly as
    // before Bug 35041 split it out for speech.
    if (block.kind === 'text' || block.kind === 'code') {
      textBuffer.push(...block.lines);
      continue;
    }
    flushMarkdown();
    segments.push(
      block.kind === 'table'
        ? { kind: 'table', ...block.table }
        : { kind: 'image', ...block.image }
    );
  }
  flushMarkdown();
  return segments;
}

const IMAGE_MARKDOWN = /!\[([^\]]*)]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
const LINK_MARKDOWN = /\[([^\]]*)]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
const AUTOLINK = /<((?:https?|ftp):\/\/[^>\s]+)>/g;
const BARE_URL = /(?:https?|ftp):\/\/[^\s)]+/g;
const HEADING_MARKER = /^\s{0,3}#{1,6}\s+/;
const BLOCKQUOTE_MARKER = /^\s{0,3}>\s?/;
const UNORDERED_BULLET = /^\s*[-*+]\s+/;
const ORDERED_BULLET = /^\s*\d+[.)]\s+/;
const CODE_FENCE = /^\s*(?:`{3,}|~{3,}).*$/;

// Underscore emphasis only counts between word boundaries (CommonMark's
// intraword rule): SNAKE_CASE_NAME or a_variable_name is not italic/bold.
// Without a boundary guard, `stripEmphasis('PRIVATE_SPOKEN_TEXT')` mangled
// the identifier into "PRIVATESPOKENTEXT" (regression caught by
// direct-call-diagnostics.test.cjs).
//
// Bug (ADO thread 51356, finding 1): the leading guard was originally a
// negative lookbehind assertion. JavaScriptCore on iOS < 16.4 (Roost's floor
// is 15.5, see App_Resources/iOS/build.xcconfig / Podfile) rejects that
// syntax at REGEX PARSE time — not a runtime miss, a load-time crash for the
// whole module. The trailing negative lookahead below is untouched:
// lookAHEAD has been supported since ES3/ES5; only lookBEHIND is the
// post-ES2018 feature JavaScriptCore's older versions can't parse.
// Fix: capture the leading boundary (start-of-string or a non-word/
// non-underscore char) as its own group instead of asserting it, and keep
// it in the replacement ('$1$2') rather than assuming it as zero-width.
const UNDERSCORE_TRIPLE = /(^|[^\w_])___([^_\n]+)___(?![\w_])/g;
const UNDERSCORE_BOLD = /(^|[^\w_])__([^_\n]+)__(?![\w_])/g;
const UNDERSCORE_ITALIC = /(^|[^\w_])_([^_\n]+)_(?![\w_])/g;

/** Strips `**`/`__`/`*`/`_` emphasis markers but keeps the wrapped text. */
function stripEmphasis(text: string): string {
  return text
    .replace(/\*\*\*([^*]+)\*\*\*/g, '$1')
    .replace(UNDERSCORE_TRIPLE, '$1$2')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(UNDERSCORE_BOLD, '$1$2')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(UNDERSCORE_ITALIC, '$1$2')
    .replace(/`([^`]+)`/g, '$1');
}

function stripLineForSpeech(line: string): string {
  let text = line
    .replace(IMAGE_MARKDOWN, (_m, alt: string) =>
      alt.trim() ? alt.trim() : 'image'
    )
    .replace(LINK_MARKDOWN, (_m, label: string) => label.trim())
    .replace(AUTOLINK, '')
    .replace(BARE_URL, '')
    .replace(HEADING_MARKER, '')
    .replace(BLOCKQUOTE_MARKER, '')
    .replace(UNORDERED_BULLET, '')
    .replace(ORDERED_BULLET, '')
    .replace(CODE_FENCE, '');
  text = stripEmphasis(text);
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Markdown -> plain speech text: link text only (never the URL), image alt
 * text (or the word "image" with no alt), a short "see the table" cue in
 * place of any pipe table instead of reading dashes and pipes, a "see the
 * code block" cue in place of fenced code, and no emphasis/heading/list
 * markers. Safe to hand straight to TTS.
 */
export function stripMarkdownForSpeech(markdown: string): string {
  const parts: string[] = [];
  for (const block of parseMarkdownLineBlocks(markdown)) {
    if (block.kind === 'table') {
      if (block.table.rows.length || block.table.headers.length) {
        parts.push('See the table for details.');
      }
      continue;
    }
    if (block.kind === 'image') {
      parts.push(block.image.alt || 'image');
      continue;
    }
    // Bug 35041: code is never read aloud (commands, JSON, keys); a cue
    // tells the listener it is on screen.
    if (block.kind === 'code') {
      if (block.body.some((line) => line.trim())) {
        parts.push('See the code block for details.');
      }
      continue;
    }
    const cleaned = block.lines
      .map(stripLineForSpeech)
      .filter((line) => line.length > 0);
    if (cleaned.length) parts.push(cleaned.join(' '));
  }
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}
